import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { User, Admin, AuthResponse } from '@/types';
import { apiHelpers, setAuthToken, clearAuth } from '@/api';
import { handleAuthError, getApiErrorMessage } from '@/utils/errorHandling';
import toast from 'react-hot-toast';

interface AuthState {
  accessToken: string | null;
  user: Admin | User | null;
  userType: 'admin' | 'brand' | 'manufacturer' | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isInitialized: boolean;
}

const initialState: AuthState = {
  accessToken: null,
  user: null,
  userType: null,
  isLoading: true, // 앱 시작 시 로딩 상태
  isAuthenticated: false,
  isInitialized: false, // 초기화 완료 여부
};

// Session flag to track if user has ever logged in (reduces unnecessary 401 calls)
const SESSION_FLAG_KEY = 'np_session_active';

export const setSessionFlag = () => {
  localStorage.setItem(SESSION_FLAG_KEY, 'true');
};

export const clearSessionFlag = () => {
  localStorage.removeItem(SESSION_FLAG_KEY);
};

const hasSessionFlag = () => {
  return localStorage.getItem(SESSION_FLAG_KEY) === 'true';
};

// backend UserInfo 에는 userType 키가 없음 (user_type 은 토큰 응답 최상위에만 존재).
// 타입 가드 isBrand/isUser 는 user.userType 존재를 전제하므로 store 저장 시점에 주입.
// admin 은 userType 키가 없어야 isAdmin 가드가 성립하므로 brand/manufacturer 만 주입.
const normalizeAuthUser = (
  user: (Admin | User) | null | undefined,
  userType: string | null | undefined
): Admin | User | null => {
  if (!user) return null;
  if (userType === 'brand' || userType === 'manufacturer') {
    return { ...user, userType } as User;
  }
  return user;
};

// Async thunks for authentication actions
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    // Skip refresh attempt if user never logged in (no session flag)
    if (!hasSessionFlag()) {
      return rejectWithValue('No previous session');
    }

    try {
      // Use centralized auth helper
      const response = await apiHelpers.auth.refresh();

      // Backend returns ApiResponse<TokenData>, so data is in response.data.data
      // Backend uses snake_case (access_token, user_type) due to Jackson SNAKE_CASE setting
      const tokenData = response.data.data;
      const accessToken = tokenData.access_token;
      const userType = tokenData.user_type;
      const adminRole = tokenData.admin_role;
      let user = tokenData.user;

      // For admin users, ensure the role field is set from adminRole
      if (userType === 'admin' && adminRole && user) {
        user = { ...user, role: adminRole };
      }

      // Set auth token for future requests
      setAuthToken(accessToken ?? null);
      // refresh 성공 시 세션 플래그 재설정 — 앞선 일시적 401 이 지운 공유 플래그를 복구해
      // 이후 새 탭이 "세션 없음"으로 오인해 로그아웃되는 캐스케이드 방지.
      setSessionFlag();

      return { accessToken: accessToken ?? null, user: user ?? null, userType: (userType ?? null) as 'admin' | 'brand' | 'manufacturer' | null };
    } catch (error: any) {
      // Handle aborted requests - don't clear auth state
      if (error.message === 'Request aborted' || error.code === 'ERR_CANCELED' || error.name === 'AbortError') {
        return rejectWithValue('Request was aborted');
      }

      // Clear auth header on failure
      clearAuth();

      // 401 — refresh 토큰 무효/만료. 단, 공유 세션 플래그는 지우지 않음:
      // 여러 탭/iframe 이 동시에 refresh 하면 rotation 경합으로 일시적 401 이 날 수 있어,
      // 여기서 플래그를 지우면 유효한 쿠키를 가진 다른 탭까지 로그아웃됨. 이번 로드만 미인증 처리.
      if (error.response?.status === 401) {
        return rejectWithValue('No active session');
      }

      return rejectWithValue(error.response?.data?.error || 'Session initialization failed');
    }
  }
);

export const adminLogin = createAsyncThunk(
  'auth/adminLogin',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await apiHelpers.auth.adminLogin(email, password);
      const tokenData = response.data.data;
      const accessToken = tokenData.access_token;
      const userType = tokenData.user_type;
      const adminRole = tokenData.admin_role;
      let user = tokenData.user;
      if (userType === 'admin' && adminRole && user) {
        user = { ...user, role: adminRole };
      }
      setAuthToken(accessToken ?? null);
      setSessionFlag();
      return { accessToken: accessToken ?? null, user: user ?? null, userType: (userType ?? null) as 'admin' | 'brand' | 'manufacturer' | null };
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error.response?.data, error.message || 'Login failed'));
    }
  }
);

export const brandLogin = createAsyncThunk(
  'auth/brandLogin',
  async ({ email, password }: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await apiHelpers.auth.brandLogin(email, password);
      const tokenData = response.data.data;
      const accessToken = tokenData.access_token;
      const userType = tokenData.user_type;
      const user = tokenData.user;
      setAuthToken(accessToken ?? null);
      setSessionFlag();
      return { accessToken: accessToken ?? null, user: user ?? null, userType: (userType ?? null) as 'admin' | 'brand' | 'manufacturer' | null };
    } catch (error: any) {
      return rejectWithValue(getApiErrorMessage(error.response?.data, error.message || 'Login failed'));
    }
  }
);

export const sendVerificationCode = createAsyncThunk(
  'auth/sendVerificationCode',
  async ({ email, purpose }: { email: string; purpose: string }, { rejectWithValue }) => {
    try {
      // 이메일 정규화 제거 - 원본 이메일 유지
      
      await apiHelpers.auth.sendVerificationCode(email, purpose);
      
      return { email };
    } catch (error: any) {
      return rejectWithValue(handleAuthError(error, 'Failed to send verification code'));
    }
  }
);

export const verifyVerificationCode = createAsyncThunk(
  'auth/verifyVerificationCode',
  async ({ email, code }: { email: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await apiHelpers.auth.verifyVerificationCode(email, code);

      // Backend returns ApiResponse<TokenData>, so data is in response.data.data
      // Backend uses snake_case (access_token, user_type) due to Jackson SNAKE_CASE setting
      const tokenData = response.data.data;
      if (!tokenData) {
        throw new Error('Invalid response from server');
      }

      const tokenDataAny = tokenData as any;
      const newUser = tokenDataAny.new_user;

      // 신규 사용자: registration_token으로 자동 register-brand 호출하여 즉시 로그인 처리
      // 이메일 prefix를 company_name/contact_person 기본값으로 사용 (Login.tsx와 동일 정책)
      if (newUser) {
        const emailPrefix = email.split('@')[0];
        const registrationToken = tokenDataAny.registration_token;

        const registerResponse = await apiHelpers.auth.registerBrand({
          email,
          registration_token: registrationToken || '',
          company_name: emailPrefix,
          contact_person: emailPrefix,
          phone: undefined,
        });

        const regData: any = registerResponse.data?.data;
        if (!regData) {
          throw new Error('Registration response missing token data');
        }

        const accessToken = regData.access_token;
        const userType = regData.user_type;
        const user = regData.user;

        setAuthToken(accessToken);
        setSessionFlag();
        toast.success('Email verified and account created!');

        return {
          accessToken: accessToken ?? null,
          user: user ?? null,
          userType: (userType ?? null) as 'admin' | 'brand' | 'manufacturer' | null,
          newUser: false,
        };
      }

      // Handle both snake_case (from backend) and camelCase field names
      const accessToken = tokenData.access_token;
      const userType = tokenData.user_type;
      const adminRole = tokenData.admin_role;
      let user = tokenData.user;

      // For admin users, ensure the role field is set from adminRole
      if (userType === 'admin' && adminRole && user) {
        user = { ...user, role: adminRole };
      }

      // API 헤더에 토큰 설정
      setAuthToken(accessToken);

      // Set session flag for future page reloads
      setSessionFlag();

      toast.success('Login successful!');
      return { accessToken: accessToken ?? null, user: user ?? null, userType: (userType ?? null) as 'admin' | 'brand' | 'manufacturer' | null, newUser: false };
    } catch (error: any) {
      return rejectWithValue(handleAuthError(error, 'Invalid verification code'));
    }
  }
);


export const logoutUser = createAsyncThunk(
  'auth/logout',
  async () => {
    try {
      // 서버에 로그아웃 요청 (httpOnly 쿠키 삭제)
      await apiHelpers.auth.logout();
    } catch {
      // Server logout may fail if already logged out - ignore
    } finally {
      // 항상 클라이언트 상태 클리어
      clearAuth();
      clearSessionFlag();
      toast.success('Logged out successfully');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    clearAuthState: (state) => {
      state.accessToken = null;
      state.user = null;
      state.userType = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isInitialized = true;
    },
    updateUser: (state, action: PayloadAction<(Admin | User) & { userType?: string }>) => {
      const { userType, ...user } = action.payload;
      // payload 에 userType 이 없으면 기존 state.userType 기준으로 재주입 (프로필 갱신 시 가드 무력화 방지)
      state.user = normalizeAuthUser(user as Admin | User, userType ?? state.userType);
      if (userType) {
        state.userType = userType as 'admin' | 'brand' | 'manufacturer';
      }
      state.isInitialized = true;
    },
    setAuthenticated: (state, action: PayloadAction<{ accessToken: string; user: Admin | User; userType: 'admin' | 'brand' | 'manufacturer' }>) => {
      state.accessToken = action.payload.accessToken;
      state.user = normalizeAuthUser(action.payload.user, action.payload.userType);
      state.userType = action.payload.userType;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.isInitialized = true;
    }
  },
  extraReducers: (builder) => {
    // Initialize auth
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
        state.isInitialized = false;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.user = normalizeAuthUser(action.payload.user, action.payload.userType);
        state.userType = action.payload.userType;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isInitialized = true;
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.accessToken = null;
        state.user = null;
        state.userType = null;
        state.isAuthenticated = false;
        state.isLoading = false;
        state.isInitialized = true;
      });

    // Admin login
    builder
      .addCase(adminLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(adminLogin.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.user = normalizeAuthUser(action.payload.user, action.payload.userType);
        state.userType = action.payload.userType;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isInitialized = true; // 로그인 성공 시 초기화 완료
      })
      .addCase(adminLogin.rejected, (state) => {
        state.isLoading = false;
      });

    // Brand login
    builder
      .addCase(brandLogin.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(brandLogin.fulfilled, (state, action) => {
        state.accessToken = action.payload.accessToken;
        state.user = normalizeAuthUser(action.payload.user, action.payload.userType);
        state.userType = action.payload.userType;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isInitialized = true;
      })
      .addCase(brandLogin.rejected, (state) => {
        state.isLoading = false;
      });

    // Verify verification code
    builder
      .addCase(verifyVerificationCode.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyVerificationCode.fulfilled, (state, action) => {
        // Don't set authentication state if user needs to register
        if (action.payload.newUser) {
          state.isLoading = false;
          return;
        }
        state.accessToken = action.payload.accessToken ?? null;
        state.user = normalizeAuthUser(action.payload.user, action.payload.userType);
        state.userType = action.payload.userType ?? null;
        state.isAuthenticated = true;
        state.isLoading = false;
        state.isInitialized = true; // 로그인 성공 시 초기화 완료
      })
      .addCase(verifyVerificationCode.rejected, (state) => {
        state.isLoading = false;
      });


    // Logout
    builder
      .addCase(logoutUser.fulfilled, (state) => {
        state.accessToken = null;
        state.user = null;
        state.userType = null;
        state.isAuthenticated = false;
        state.isLoading = false;
      });
  },
});

export const {
  setLoading,
  clearAuthState,
  updateUser,
  setAuthenticated
} = authSlice.actions;

export default authSlice.reducer;